/**
 * Refresh the open Diligence template's field values from the server.
 *
 * A field write that did not originate in this browser never touches this
 * Apollo cache: an approved mutation plan executes server-side
 * (MutationPlan.mutator runs the mutation through the server's own Apollo), and
 * so do the agent's MCP tool calls and the Ranger drafting worker. Without this
 * the open form sits stale until the 60s `pollInterval` on the template store's
 * watchQueries, which reads to the user as "the agent said it saved but nothing
 * happened".
 *
 * The template store registers a callback that refetches its own four
 * ObservableQuery instances (published / mgr / rangerGenerated / draft) —
 * rather than going through `client.refetchQueries({ include: "active" })` and
 * matching on operation name, which couples the refresh to a dynamically built
 * query's name and to Apollo's notion of which queries count as "active".
 * Refetching the exact observables the form subscribes to is the only thing
 * that provably re-emits into the form.
 *
 * ponytail: module-level registry, not React context — the chat and the
 * mutation-plan card both live outside the per-page TemplateStoreProvider.
 */
type TemplateFieldRefetch = () => void;

const refetchers = new Set<TemplateFieldRefetch>();

/** Register a refetcher; returns the deregister function for cleanup. */
export function registerTemplateFieldRefetch(
  refetch: TemplateFieldRefetch
): () => void {
  refetchers.add(refetch);
  return () => {
    refetchers.delete(refetch);
  };
}

/** Refetch every mounted Diligence template's field values. */
export function refetchTemplateFields(): void {
  refetchers.forEach(refetch => refetch());
}

/** Test seam: how many templates are currently listening. */
export function templateFieldRefetchCount(): number {
  return refetchers.size;
}
